<script setup lang="ts">
import { PageMeta } from '@/@vxapp/@Enums/PageMeta'
import { preferredScrollBehavior } from '@/utils/scrollBehavior'
import {
  getPricingAvailabilityIcon,
  getPricingAvailabilityLabel,
  pricingFeatures,
  pricingPlans,
  type PricingPlanId,
} from './pricingComparison'

definePage({ meta: { ...PageMeta.Dashboard.ecommerce.meta, pageTitle: 'Pricing' } })

const faqs = [
  {
    question: 'What counts towards the 100 responses limit?',
    answer:
      'Each completed form submission counts as one response. Drafts and internal previews do not consume the response allowance.',
  },
  {
    question: 'How do you process payments?',
    answer:
      'We accept Visa, Mastercard, American Express, and PayPal through our secure payment provider.',
  },
  {
    question: 'What payment methods do you accept?',
    answer: 'All major credit and debit cards are supported.',
  },
  {
    question: 'Do you have a money-back guarantee?',
    answer: 'Yes. You may request a refund within 48 hours of your purchase.',
  },
]

const selectedPlan = ref<PricingPlanId | null>(null)

const selectedPlanName = computed(
  () => pricingPlans.find(plan => plan.id === selectedPlan.value)?.name ?? ''
)

const handlePlanSelect = (planId: PricingPlanId) => {
  selectedPlan.value = planId
}

const handleTrialSelect = () => {
  selectedPlan.value = 'basic'
  document.querySelector('[data-testid="pricing-plan-basic"]')?.scrollIntoView({
    behavior: preferredScrollBehavior(),
    block: 'center',
  })
}
</script>

<template>
  <main class="pricing-page">
    <section class="pricing-hero">
      <div class="pricing-hero__label">Plans · transparent by design</div>
      <h1>Infrastructure that grows with your operation.</h1>
      <p>
        Start with the full platform, then choose the support and retention model that matches your
        team.
      </p>
      <VBtn
        color="primary"
        size="large"
        @click="handleTrialSelect"
      >
        Start 14-day free trial
      </VBtn>
    </section>

    <section
      aria-labelledby="pricing-plans-title"
      class="pricing-section"
    >
      <div class="pricing-section__intro">
        <div>
          <div class="pricing-section__label">Plan overview</div>
          <h2 id="pricing-plans-title">Choose the operating model that fits.</h2>
        </div>
        <p>All plans begin with a 14-day trial. Paid selections retain a 48-hour refund window.</p>
      </div>

      <div class="pricing-plan-grid">
        <article
          v-for="plan in pricingPlans"
          :key="plan.id"
          class="pricing-plan"
          :class="{ 'pricing-plan--recommended': plan.recommended }"
          :data-testid="`pricing-plan-${plan.id}`"
        >
          <div class="pricing-plan__topline">
            <span class="pricing-plan__name">{{ plan.name }}</span>
            <VChip
              v-if="plan.recommended"
              color="primary"
              size="small"
              variant="tonal"
            >
              Recommended
            </VChip>
          </div>
          <div class="pricing-plan__price">{{ plan.price }}</div>
          <div class="pricing-plan__suffix">{{ plan.priceSuffix }}</div>
          <p>{{ plan.description }}</p>
          <VBtn
            :aria-pressed="selectedPlan === plan.id"
            block
            :color="plan.recommended ? 'primary' : undefined"
            :variant="plan.recommended ? 'flat' : 'outlined'"
            @click="() => handlePlanSelect(plan.id)"
          >
            {{ selectedPlan === plan.id ? `${plan.name} selected` : `Choose ${plan.name}` }}
          </VBtn>
        </article>
      </div>

      <VAlert
        v-if="selectedPlan"
        class="mt-6"
        data-testid="pricing-selection-status"
        type="info"
        variant="tonal"
      >
        {{ selectedPlanName }} is selected. Checkout is not configured on this comparison page.
      </VAlert>
    </section>

    <section
      aria-labelledby="pricing-comparison-title"
      class="pricing-section pricing-section--comparison"
      data-testid="pricing-comparison"
    >
      <div class="pricing-section__intro">
        <div>
          <div class="pricing-section__label">Feature matrix</div>
          <h2 id="pricing-comparison-title">Compare what each plan includes.</h2>
        </div>
        <p>Every feature from the previous comparison remains visible, including paid add-ons.</p>
      </div>

      <div class="pricing-comparison">
        <div
          aria-hidden="true"
          class="pricing-comparison__heading"
        >
          <span>Capability</span>
          <span
            v-for="plan in pricingPlans"
            :key="plan.id"
          >
            {{ plan.name }}
          </span>
        </div>

        <article
          v-for="(feature, featureIndex) in pricingFeatures"
          :key="feature.name"
          class="pricing-comparison__row"
          :data-testid="`pricing-feature-${featureIndex}`"
        >
          <div class="pricing-comparison__feature">{{ feature.name }}</div>
          <div
            v-for="plan in pricingPlans"
            :key="plan.id"
            class="pricing-comparison__availability"
          >
            <span class="pricing-comparison__mobile-label">{{ plan.name }}</span>
            <VIcon
              :color="
                feature.availability[plan.id] === 'included'
                  ? 'success'
                  : feature.availability[plan.id] === 'add-on'
                    ? 'primary'
                    : undefined
              "
              :icon="getPricingAvailabilityIcon(feature.availability[plan.id])"
              size="18"
            />
            <span>{{ getPricingAvailabilityLabel(feature.availability[plan.id]) }}</span>
          </div>
        </article>

        <div class="pricing-comparison__actions">
          <span>Plan decision</span>
          <VBtn
            v-for="plan in pricingPlans"
            :key="plan.id"
            :aria-label="`Choose ${plan.name} from comparison`"
            :color="plan.recommended ? 'primary' : undefined"
            :variant="plan.recommended ? 'flat' : 'text'"
            @click="() => handlePlanSelect(plan.id)"
          >
            Choose {{ plan.name }}
          </VBtn>
        </div>
      </div>
    </section>

    <section class="pricing-trial-band">
      <div>
        <div class="pricing-section__label">Try every feature</div>
        <h2>Still deciding? Use the complete platform for 14 days.</h2>
        <p>No user limit during the trial. Choose a paid plan only when the fit is clear.</p>
      </div>
      <VBtn
        color="primary"
        size="large"
        @click="handleTrialSelect"
      >
        Start free trial
      </VBtn>
    </section>

    <section
      aria-labelledby="pricing-faq-title"
      class="pricing-section pricing-faq"
    >
      <div class="pricing-section__intro">
        <div>
          <div class="pricing-section__label">FAQ</div>
          <h2 id="pricing-faq-title">Common questions, clearly answered.</h2>
        </div>
      </div>
      <VExpansionPanels variant="accordion">
        <VExpansionPanel
          v-for="faq in faqs"
          :key="faq.question"
          :text="faq.answer"
          :title="faq.question"
        />
      </VExpansionPanels>
    </section>
  </main>
</template>

<style scoped>
.pricing-page {
  min-height: 100%;
  padding: clamp(16px, 4vw, 56px);
  background:
    radial-gradient(circle at 92% 4%, rgba(var(--v-theme-primary), 0.12), transparent 32rem),
    rgb(var(--v-theme-background));
  color: rgb(var(--v-theme-on-background));
}

.pricing-hero,
.pricing-section,
.pricing-trial-band {
  max-width: 1180px;
  margin-inline: auto;
}

.pricing-hero {
  padding-block: clamp(56px, 9vw, 112px);
  text-align: center;
}

.pricing-hero__label,
.pricing-section__label {
  margin-block-end: 12px;
  color: rgb(var(--v-theme-primary));
  font-family: 'JetBrains Mono', ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  font-size: 0.72rem;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.pricing-hero h1 {
  max-width: 860px;
  margin: 0 auto 24px;
  font-family: Georgia, 'Times New Roman', serif;
  font-size: clamp(2.5rem, 7vw, 4.5rem);
  font-weight: 400;
  letter-spacing: -0.035em;
  line-height: 1;
}

.pricing-hero p {
  max-width: 620px;
  margin: 0 auto 32px;
  color: rgba(var(--v-theme-on-background), var(--v-medium-emphasis-opacity));
  font-size: 1.05rem;
  line-height: 1.6;
}

.pricing-section {
  padding-block: 48px;
}

.pricing-section__intro {
  display: flex;
  align-items: end;
  justify-content: space-between;
  gap: 32px;
  margin-block-end: 28px;
}

.pricing-section__intro h2,
.pricing-trial-band h2 {
  max-width: 700px;
  margin: 0;
  font-family: Georgia, 'Times New Roman', serif;
  font-size: clamp(2rem, 4vw, 3.25rem);
  font-weight: 400;
  letter-spacing: -0.025em;
  line-height: 1.08;
}

.pricing-section__intro > p {
  max-width: 360px;
  margin: 0;
  color: rgba(var(--v-theme-on-background), var(--v-medium-emphasis-opacity));
}

.pricing-plan-grid {
  display: grid;
  gap: 16px;
  grid-template-columns: repeat(3, minmax(0, 1fr));
}

.pricing-plan {
  display: flex;
  min-width: 0;
  flex-direction: column;
  padding: 28px;
  border: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  border-radius: 22px;
  background: rgb(var(--v-theme-surface));
}

.pricing-plan--recommended {
  border-color: rgba(var(--v-theme-primary), 0.5);
}

.pricing-plan__topline {
  display: flex;
  min-height: 32px;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.pricing-plan__name {
  font-size: 1.15rem;
}

.pricing-plan__price {
  margin-block-start: 36px;
  font-family: Georgia, 'Times New Roman', serif;
  font-size: clamp(2.5rem, 5vw, 4rem);
  letter-spacing: -0.04em;
  line-height: 1;
}

.pricing-plan__suffix {
  margin-block-start: 6px;
  color: rgba(var(--v-theme-on-surface), var(--v-medium-emphasis-opacity));
  font-size: 0.8rem;
}

.pricing-plan p {
  min-height: 72px;
  margin-block: 24px 32px;
  color: rgba(var(--v-theme-on-surface), var(--v-medium-emphasis-opacity));
  line-height: 1.5;
}

.pricing-plan :deep(.v-btn) {
  min-height: 48px;
  margin-block-start: auto;
}

.pricing-section--comparison {
  padding-block-start: 72px;
}

.pricing-comparison {
  overflow: hidden;
  border: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  border-radius: 22px;
  background: rgb(var(--v-theme-surface));
}

.pricing-comparison__heading,
.pricing-comparison__row,
.pricing-comparison__actions {
  display: grid;
  align-items: center;
  grid-template-columns: minmax(220px, 1.35fr) repeat(3, minmax(140px, 1fr));
}

.pricing-comparison__heading {
  min-height: 72px;
  padding-inline: 24px;
  border-bottom: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  background: rgba(var(--v-theme-on-surface), 0.025);
  font-family: 'JetBrains Mono', ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  font-size: 0.7rem;
  letter-spacing: 0.07em;
  text-transform: uppercase;
}

.pricing-comparison__heading span:not(:first-child) {
  text-align: center;
}

.pricing-comparison__row {
  min-height: 68px;
  padding-inline: 24px;
}

.pricing-comparison__row + .pricing-comparison__row {
  border-top: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
}

.pricing-comparison__feature {
  padding-block: 16px;
}

.pricing-comparison__availability {
  display: flex;
  min-width: 0;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 12px;
  color: rgba(var(--v-theme-on-surface), var(--v-medium-emphasis-opacity));
  font-size: 0.82rem;
  text-align: center;
}

.pricing-comparison__mobile-label {
  display: none;
}

.pricing-comparison__actions {
  min-height: 72px;
  padding-inline: 24px;
  border-top: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  background: rgba(var(--v-theme-on-surface), 0.025);
}

.pricing-comparison__actions :deep(.v-btn) {
  min-height: 44px;
}

.pricing-trial-band {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 32px;
  padding: clamp(32px, 6vw, 64px);
  border-radius: 22px;
  background:
    linear-gradient(135deg, rgba(var(--v-theme-primary), 0.2), transparent 65%),
    rgb(var(--v-theme-surface));
}

.pricing-trial-band p {
  margin: 16px 0 0;
  color: rgba(var(--v-theme-on-surface), var(--v-medium-emphasis-opacity));
}

.pricing-trial-band :deep(.v-btn) {
  min-width: 180px;
  min-height: 52px;
}

.pricing-faq {
  padding-block-end: 80px;
}

@media (max-width: 959px) {
  .pricing-section__intro {
    align-items: flex-start;
    flex-direction: column;
  }

  .pricing-plan-grid {
    grid-template-columns: 1fr;
  }

  .pricing-plan p {
    min-height: 0;
  }

  .pricing-comparison {
    border: 0;
    border-radius: 0;
    background: transparent;
  }

  .pricing-comparison__heading {
    display: none;
  }

  .pricing-comparison__row {
    display: grid;
    margin-block-end: 12px;
    padding: 20px;
    border: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
    border-radius: 22px;
    background: rgb(var(--v-theme-surface));
    grid-template-columns: 1fr;
  }

  .pricing-comparison__row + .pricing-comparison__row {
    border-top: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  }

  .pricing-comparison__feature {
    padding-block: 0 16px;
    font-size: 1rem;
  }

  .pricing-comparison__availability {
    justify-content: flex-start;
    padding: 10px 0;
    border-top: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
    text-align: start;
  }

  .pricing-comparison__mobile-label {
    display: inline;
    min-width: 88px;
    color: rgb(var(--v-theme-on-surface));
    font-family: 'JetBrains Mono', ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
    font-size: 0.68rem;
    letter-spacing: 0.06em;
    text-transform: uppercase;
  }

  .pricing-comparison__actions {
    display: grid;
    padding: 20px;
    border: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
    border-radius: 22px;
    grid-template-columns: 1fr;
  }

  .pricing-comparison__actions > span {
    margin-block-end: 8px;
  }

  .pricing-trial-band {
    align-items: flex-start;
    flex-direction: column;
  }
}

@media (max-width: 599px) {
  .pricing-page {
    padding-inline: 12px;
  }

  .pricing-section {
    padding-block: 36px;
  }

  .pricing-plan {
    padding: 22px;
  }

  .pricing-trial-band :deep(.v-btn) {
    width: 100%;
  }
}
</style>
